import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";
import UploadFile from "components/_refactor/dropzone";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  MATERIAL_TYPES,
  CAMPAIGN_CATEGORIES,
  CONTENT_STATUS,
  SEGMENTATION_OPTIONS,
  MOCK_CONTENTS,
} from "../constants";

const EMPTY_FORM = {
  nome: "",
  tipo: null,
  categoria: null,
  campanha: null,
  descricao: "",
  dataInicio: "",
  dataFim: "",
  status: null,
  segmentacao: null,
};

const campanhaOptions = MOCK_CONTENTS.filter((c) => c.tipo === "CAMPANHA").map(
  (c) => ({ value: c.id, label: c.titulo }),
);

export default function DialogMaterial({ open, onClose, content, presetTipo }) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [arquivoFiles, setArquivoFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (content) {
      setForm({
        nome: content.titulo || "",
        tipo: MATERIAL_TYPES.find((t) => t.value === "PDF") || null,
        categoria:
          CAMPAIGN_CATEGORIES.find((c) => c.label === content.categoria) ||
          null,
        campanha: null,
        descricao: content.descricao || "",
        dataInicio: "",
        dataFim: "",
        status: CONTENT_STATUS.find((s) => s.value === content.status) || null,
        segmentacao: null,
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        tipo: presetTipo
          ? MATERIAL_TYPES.find((t) => t.value === presetTipo) || null
          : null,
      });
    }
  }, [open, content, presetTipo]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome do material é obrigatório.", {
        severity: "error",
      });
      return;
    }
    snackbar.showMessage(
      content
        ? "Material atualizado com sucesso."
        : "Material salvo com sucesso.",
      { severity: "success" },
    );
    onClose();
  };

  const isLookbook = presetTipo === "LOOKBOOK" || content?.tipo === "LOOKBOOK";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle style={{ color: themeColorsDesingSystem.text.primary }}>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content
            ? `Editar ${isLookbook ? "Lookbook" : "Material"}`
            : `Cadastro de ${isLookbook ? "Lookbook" : "Material"}`}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label={isLookbook ? "Nome do lookbook" : "Nome do material"}
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>
              {!isLookbook && (
                <Grid item xs={12}>
                  <AutoCompleteField
                    label="Tipo de material"
                    value={form.tipo}
                    onChange={(v) => updateForm("tipo", v)}
                    options={MATERIAL_TYPES}
                    optionLabelKey="label"
                    optionIdKey="value"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={CAMPAIGN_CATEGORIES}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Campanha vinculada"
                  value={form.campanha}
                  onChange={(v) => updateForm("campanha", v)}
                  options={campanhaOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Vigência início"
                  value={form.dataInicio}
                  onChange={(v) => updateForm("dataInicio", v)}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Vigência fim"
                  value={form.dataFim}
                  onChange={(v) => updateForm("dataFim", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Segmentação"
                  value={form.segmentacao}
                  onChange={(v) => updateForm("segmentacao", v)}
                  options={SEGMENTATION_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Arquivo
                </Typography>
                <UploadFile
                  message="Arraste o arquivo ou clique para selecionar"
                  accept={{
                    "application/pdf": [".pdf"],
                    "image/*": [".jpg", ".jpeg", ".png"],
                    "video/mp4": [".mp4"],
                    "application/zip": [".zip"],
                  }}
                  onChangeFiles={setArquivoFiles}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={{ "image/*": [".jpg", ".jpeg", ".png"] }}
                  onChangeFiles={setThumbnailFiles}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Salvar {isLookbook ? "lookbook" : "material"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
